/**
 * globo-grid-bridge.js — puente entre la grilla de la app Globo y el theme.
 *
 * La grilla de colecciones y el buscador los renderiza Globo con su propio motor
 * (snippets/globo.filter.product.liquid esta envuelto en {% raw %}: los filtros de
 * adentro NO los ejecuta Shopify).
 *
 * El boton de agregar al carrito de la card es un <form> POST plano, ajeno a los custom
 * elements del theme (<product-form> ni siquiera esta registrado en las paginas de
 * coleccion: product.js no se carga ahi). Sin esto, agregar desde la grilla hace
 * POST -> 302 -> navegacion completa a /cart, sin cart drawer.
 *
 * Idempotente y sin dependencias: app.js carga en todas las paginas. El submit va por
 * delegacion, asi que sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
 *
 * NO intenta reformatear los precios de la card, y NO hace falta.
 *
 * En ESTA tienda (USD) el formato nativo de Globo ya es el correcto: verificado en
 * vivo el 2026-07-29 sobre /collections/all, renderiza "$2,180.00". Por eso aca no
 * hay ningun arreglo de separador y su ausencia no es un olvido.
 *
 * En la rama `master` (ARS) si hace falta, porque el peso escribe "$1.092.630" con
 * punto. Ahi se resuelve con un filtro Liquid dentro de la plantilla de Globo
 * (`| money | replace: ',', '.'` en globo.filter.product.liquid), no en este archivo.
 * O sea: NO portar ese replace a `us`, romperia el formato de USD.
 *
 * Queda desmentida de paso la premisa vieja de que "el formato no sale de
 * shop.money_format". Si sale de ahi: el valor es "${{amount_no_decimals}}" y Shopify
 * elige el separador segun la MONEDA de la tienda, no segun el nombre del placeholder
 * -por eso la misma plantilla imprime punto en ARS y coma en USD-.
 *
 * Lo que SI sigue vigente como advertencia: el motor de Globo entrega el precio en
 * UNIDADES DE MONEDA, no en centavos (`{{ variant.price }}` -> 4378742, no
 * 437874200), mientras que `formatMoney` del theme espera centavos. Pasar uno al otro
 * ya rompio una vez: la grilla anunciaba $43.787 para un producto de $4.378.742 y el
 * carrito cobraba el precio real. Si algun dia hay que tocar el formato, el camino
 * seguro es reescribir SEPARADORES sobre los digitos ya renderizados, nunca recalcular.
 */
(() => {
  'use strict';

  const SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer--inner' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
  ];

  /* -------------------------------------------------- agregar al carrito */

  const renderContents = (parsedState) => {
    const drawerHtml = parsedState.sections?.['cart-drawer'];
    if (!drawerHtml || !new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('.cart-drawer--inner')) return false;
    SECTIONS.forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return; // en coleccion no existe #Cart (main-cart): se saltea
      const sourceHtml = parsedState.sections && parsedState.sections[section.section];
      if (!sourceHtml) return;

      const inner = new DOMParser()
        .parseFromString(sourceHtml, 'text/html')
        .querySelector(section.selector);
      if (!inner) return;

      const elementToReplace = target.querySelector(section.selector) || target;
      // innerHTML es EXCEPCION consciente a la regla del proyecto (ver docs/frontend.md):
      // el dato es HTML de la Section Rendering API de Shopify, no texto, asi que
      // textContent no aplica. Y NO cambiar por replaceChildren(...inner.childNodes):
      // innerHTML nunca ejecuta <script>, pero mover nodos de DOMParser al documento
      // vivo SI los ejecuta -- seria abrir un vector que hoy no existe.
      elementToReplace.innerHTML = inner.innerHTML;
    });

    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open();
    if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
    return true;
  };

  const setLoading = (form, loading) => {
    const button = form.querySelector('.spf-product__form-btn-addtocart');
    if (!button) return;
    button.classList.toggle('loading', loading);
    if (loading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  };

  const onSubmit = (event) => {
    const form = event.target.closest('.spf-product__form');
    if (!form) return;

    // Si el drawer esta apagado en el editor, el POST nativo a /cart es el
    // comportamiento correcto: no hay drawer que abrir.
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || typeof drawer.open !== 'function' || !window.CollinoCart) return;
    if (window.theme && window.theme.settings && window.theme.settings.cart_drawer === false) return;
    if (typeof theme === 'undefined' || !theme.routes || !theme.routes.cart_add_url) return;

    event.preventDefault();
    if (form.dataset.submitting === 'true') return; // dos toques rapidos = dos unidades
    form.dataset.submitting = 'true';
    setLoading(form, true);

    CollinoCart.clearError(form);
    const button = form.querySelector('.spf-product__form-btn-addtocart');
    const formData = new FormData(form);
    formData.append('sections', SECTIONS.map((section) => section.section).join(','));
    formData.append('sections_url', window.location.pathname);
    let added = false;
    return CollinoCart.enqueue(async () => {
      const response = await fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData
      });
      const state = await response.json();
      if (!response.ok || state.status || state.errors) throw new Error(CollinoCart.message(state, "Could not confirm the item was added. Check your cart before trying again."));
      added = true;
      if (!renderContents(state)) window.location.assign(CollinoCart.cartUrl());
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart:item-added', { product: state.items ? state.items[0] : state });
      }
    }).catch((error) => {
      // Despues del primer POST, el fallback solo puede leer o navegar al carrito.
      // Una respuesta ilegible puede corresponder a una escritura ya aceptada.
      if (added) window.location.assign(CollinoCart.cartUrl());
      else CollinoCart.showError(form, button, error instanceof SyntaxError || error instanceof TypeError ? "Could not confirm the item was added. Check your cart before trying again." : error.message);
    }).finally(() => {
      form.dataset.submitting = 'false';
      setLoading(form, false);
    });
  };

  // Navigation overlays share their Escape/focus lifecycle in navigation-panels.js.

  /* ------------------------------------- limpiar busqueda interna Globo */

  const getUrlWithoutGloboSearch = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('options[prefix]');
    url.searchParams.delete('options%5Bprefix%5D');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const syncSearchClear = () => {
    const grid = document.getElementById('gf-products');
    if (!grid) return;

    const existing = document.querySelector('[data-globo-search-clear]');
    const params = new URLSearchParams(window.location.search);
    const hasSearch = params.get('q');
    const productCount = grid.querySelectorAll('.spf-product-card-wrapper').length;
    const shouldShow = Boolean(hasSearch) && productCount === 0;

    if (!shouldShow) {
      if (existing) existing.remove();
      return;
    }

    if (existing) {
      existing.setAttribute('href', getUrlWithoutGloboSearch());
      return;
    }

    const emptyMessage = grid.querySelector('.empty, .gf-empty, [class*="empty"], [class*="no-result"]') || grid;
    const clearLink = document.createElement('a');
    clearLink.href = getUrlWithoutGloboSearch();
    clearLink.className = 'globo-search-clear';
    clearLink.dataset.globoSearchClear = 'true';
    clearLink.textContent = 'Clear search';
    emptyMessage.insertAdjacentElement('afterend', clearLink);
  };

  // Globo restores results on history navigation but leaves the sort label stale.
  // Read its configured default (not the first option) and reuse its translated labels.
  // This only reflects state: never click an option or request another product grid.
  const syncSortControl = () => {
    const button = document.querySelector('#sortButton');
    // The app replaces the initial span with a text node after a user selection.
    const label = button?.querySelector('span') || button;
    const options = document.querySelectorAll('#globo-dropdown-sort_options [data-sort]');
    const sort = new URLSearchParams(window.location.search).get('sort_by')
      || window.GloboEmbedFilterConfig?.collection?.sort;
    const selected = [...options].find((option) => option.dataset.sort === sort);
    if (!label || !selected) return;
    const text = selected.textContent.trim();
    if (label.textContent !== text) label.textContent = text;
    options.forEach((option) => {
      option.setAttribute('aria-selected', String(option === selected));
      option.tabIndex = option === selected ? 0 : -1;
    });
  };

  const collectionSearchSort = new Map();
  const initialCollectionSort = new URLSearchParams(location.search).get('sort_by');
  if (initialCollectionSort && initialCollectionSort !== 'relevance') collectionSearchSort.set(location.pathname, initialCollectionSort);
  // Presentation only; keep app controls and delegated handlers intact.
  const syncCollectionFilterAppearance = () => {
    if (!document.body.classList.contains('template-collection')) return;
    const controls = document.getElementById('gf-controls-container');
    if (!controls) return;
    const explicitSort = new URLSearchParams(location.search).get('sort_by');
    if (explicitSort && explicitSort !== 'relevance') collectionSearchSort.set(location.pathname, explicitSort);
    const sort = controls.querySelector('.gf-filter-selection');
    const filter = controls.querySelector('.gf-filter-trigger');
    const filterButton = filter?.querySelector('button');
    if (filter && filterButton) {
      if (!filter.querySelector('[data-collection-filter-label]')) {
        const label = document.createElement('span');
        label.dataset.collectionFilterLabel = '';
        label.textContent = 'Filter by';
        filter.prepend(label);
      }
      const caption = 'Choose filters';
      const text = [...filterButton.childNodes].find((node) => node.nodeType === 3 && node.textContent.trim());
      if (text && text.textContent.trim() !== caption) text.textContent = ' ' + caption;
      const accessibleLabel = 'Filter by: Choose filters';
      if (filterButton.getAttribute('aria-label') !== accessibleLabel) filterButton.setAttribute('aria-label', accessibleLabel);
    }
    if (sort && !sort.querySelector('[data-collection-sort-label]')) {
      const label = document.createElement('span');
      label.dataset.collectionSortLabel = ''; label.textContent = 'Sort by'; sort.prepend(label);
    }
    const searchLabel = controls.querySelector('#gf-controls-search-label');
    if (searchLabel) searchLabel.classList.remove('sr-only');
    const search = controls.querySelector('#gf-controls-search-input');
    if (search && search.placeholder !== 'Search this collection') search.placeholder = 'Search this collection';
    const clear = controls.querySelector('.gf-controls-clear-button');
    if (clear && clear.getAttribute('aria-label') !== 'Clear search') clear.setAttribute('aria-label', 'Clear search');
    if (clear) clear.hidden = !search?.value;
  };

  // The app clears sort_by together with q. Use the existing collection route
  // for this action so filters and the last explicit sort survive the reset.
  const onCollectionSearchClear = (event) => {
    if (!document.body.classList.contains('template-collection')) return;
    const sortOption = event.target.closest?.('#globo-dropdown-sort_options [data-sort]');
    if (sortOption && sortOption.dataset.sort !== 'relevance') collectionSearchSort.set(location.pathname, sortOption.dataset.sort);
    if (!event.target.closest?.('#gf-controls-container .gf-controls-clear-button')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const url = new URL(location.href);
    url.searchParams.delete('q'); url.searchParams.delete('page');
    const sort = collectionSearchSort.get(url.pathname);
    if ((!url.searchParams.has('sort_by') || url.searchParams.get('sort_by') === 'relevance') && sort) url.searchParams.set('sort_by', sort);
    location.assign(url.href);
  };

  // Globo delegates option clicks/keyboard handling and accepts price-ascending.
  // Extend its existing list; do not sort the visible page locally or replace
  // the toolbar (which would discard search/filter state and app listeners).
  const ensureAscendingSort = () => {
    syncCollectionFilterAppearance();
    const list = document.querySelector('#globo-dropdown-sort_options');
    if (!list) return;
    const existing = list.querySelector('[data-sort="price-ascending"]');
    const button = document.querySelector('#sortButton');
    const label = button?.querySelector('span') || button;
    // The app may regenerate the option from its translations on reload.
    if (label?.textContent.trim() === 'Price, low to high') {
      label.textContent = 'Price: low to high';
    }
    if (existing) {
      if (existing.textContent !== 'Price: low to high') existing.textContent = 'Price: low to high';
      if (existing.getAttribute('aria-label') !== 'Price: low to high') existing.setAttribute('aria-label', 'Price: low to high');
      return;
    }
    const option = document.createElement('li');
    option.dataset.sort = 'price-ascending';
    option.setAttribute('role', 'option');
    option.setAttribute('aria-label', 'Price: low to high');
    option.setAttribute('aria-selected', 'false');
    option.tabIndex = -1;
    option.textContent = 'Price: low to high';
    list.prepend(option);
  };

  // Globo replaces this markup on every AJAX render. Keep its delegated links
  // intact; only annotate state and fill the omitted, non-interactive extremes.
  const syncPagination = () => {
    document.querySelectorAll('#gf_pagination_wrap #pagination').forEach((pagination) => {
      if (!pagination.querySelector('.page.current')) return;
      pagination.setAttribute('role', 'navigation');
      pagination.setAttribute('aria-label', 'Pagination');
      pagination.querySelectorAll('.page').forEach((page) => {
        if (page.classList.contains('current')) {
          page.setAttribute('aria-current', 'page');
          page.setAttribute('aria-label', 'Page ' + page.textContent.trim());
        } else {
          page.removeAttribute('aria-current');
          page.removeAttribute('aria-label');
          const link = page.querySelector('a.gspf-page');
          if (link) link.setAttribute('aria-label', 'Page ' + link.textContent.trim());
        }
      });
      [['prev', 'Previous', '←'], ['next', 'Next', '→']].forEach(([direction, label, arrow]) => {
        let control = pagination.querySelector('.' + direction);
        if (!control) {
          control = document.createElement('span');
          control.className = direction + ' disabled';
          control.dataset.globoDisabledPage = 'true';
          const state = document.createElement('span');
          state.textContent = arrow;
          state.setAttribute('role', 'link');
          state.setAttribute('aria-disabled', 'true');
          state.setAttribute('aria-label', label);
          control.append(state);
          if (direction === 'prev') pagination.prepend(control);
          else pagination.append(control);
        }
        const link = control.querySelector('a.gspf-page');
        if (link) link.setAttribute('aria-label', label);
      });
    });
  };

  const syncGridControls = () => {
    ensureAscendingSort();
    syncSearchClear();
    syncSortControl();
    syncPagination();
  };

  let controlsFrame = 0;
  // Only history needs repair. Observing every grid redraw races the app's own
  // option click, which changes the label before updating the URL.
  const scheduleGridControls = () => {
    if (controlsFrame) return;
    controlsFrame = window.requestAnimationFrame(() => {
      controlsFrame = 0;
      syncGridControls();
    });
  };

  // Values are formatted by Globo in its own currency units, never by theme.formatMoney.
  const onVariantChange = (event) => {
    const select = event.target.closest?.('.spf-product__variants');
    const card = select?.closest('.spf-product-card');
    const option = select?.selectedOptions?.[0];
    if (!card || !option || option.dataset.cardPrice === undefined) return;
    const data = option.dataset;
    const consult = data.cardConsult === 'true';
    const available = data.available === 'true';
    const hasTransfer = !consult && data.cardHasTransfer === 'true';
    const hasDiscount = !consult && data.cardHasDiscount === 'true';
    const setText = (selector, value) => {
      const node = card.querySelector(selector);
      if (node) node.textContent = value;
    };
    const setHidden = (selector, hidden) => {
      const node = card.querySelector(selector);
      if (node) node.hidden = hidden;
    };
    setHidden('[data-globo-consult]', !consult);
    setHidden('[data-globo-price]', consult);
    const consultLink = card.querySelector('.spf-product-card__consultar-btn');
    if (consultLink && data.cardConsultUrl) consultLink.href = data.cardConsultUrl;
    setText('.spf-product-card__price', data.cardPrice);
    setText('.spf-product-card__oldprice', data.cardCompare);
    setText('.spf-transfer-price', data.cardTransfer);
    setText('[data-globo-discount]', data.cardDiscount + '% OFF');
    setHidden('[data-globo-discount]', !hasDiscount);
    setHidden('[data-globo-soldout]', available);
    setHidden('.spf-price-transfer', !hasTransfer);
    const oldPrice = card.querySelector('.spf-product-card__oldprice');
    if (oldPrice) oldPrice.style.display = hasDiscount ? '' : 'none';
    card.querySelector('.spf-product-card__price-wrapper')?.classList.toggle('spf-product-card__price-wrapper--has-transfer', hasTransfer);
    card.classList.toggle('spf-soldout', !available);
    const wrapper = card.closest('.spf-product-card-wrapper');
    if (wrapper) wrapper.dataset.productoSinPrecio = String(consult);
    const form = card.querySelector('.spf-product__form');
    form?.classList.toggle('spf-product__form--hidden', consult);
    select.disabled = consult || !available;
    const addButton = card.querySelector('.spf-product__form-btn-addtocart');
    if (addButton) {
      addButton.disabled = consult || !available;
      const label = addButton.querySelector('span');
      if (label) label.textContent = available ? addButton.dataset.available : addButton.dataset.soldout;
    }
    const shippingMessage = !consult && available ? (data.cardShippingMessage || '') : '';
    const shipping = shippingMessage !== '';
    setText('[data-globo-shipping-label]', shippingMessage);
    const shippingLink = card.querySelector('[data-globo-shipping] a');
    if (shippingLink) shippingLink.dataset.shippingFree = String(shipping && data.cardShipping === 'true');
    setHidden('[data-globo-shipping] a', !shipping);
    setHidden('[data-globo-shipping] .spf-product-card__shipping--placeholder', shipping || consult);
    card.querySelectorAll('.spf-product-card__title a, a.spf-product-card__image-wrapper, .spf-product__form-btn-options').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      url.searchParams.set('variant', option.value);
      link.href = url.pathname + url.search + url.hash;
    });
  };

  // Indicacion derivada del ancho real; sobrevive a filtros y redimensionado.
  const syncVariantOverflow = () => {
    // Read every rail before changing overflow styles or inserting buttons.
    // Interleaving these phases forces a layout for each following card.
    const rails = [...document.querySelectorAll('#gf-products .spf-product-card__thumbnails')]
      .map((rail) => ({ rail, width: rail.clientWidth, scrollWidth: rail.scrollWidth, left: rail.scrollLeft }));
    rails.forEach(({ rail, width, scrollWidth, left }) => {
      const container = rail.closest('.spf-product-card__thumbnails-container');
      if (!container) return;
      const overflow = scrollWidth > width + 2;
      container.dataset.variantControls = String(rail.querySelectorAll('[data-globo-variant-thumbnail]').length > 1);
      container.dataset.variantOverflow = String(overflow);
      container.dataset.variantEnd = String(left + width >= scrollWidth - 2);
      rail.setAttribute('aria-label', 'Variants. Use the thumbnails or arrow to choose an option.');
      let button = container.querySelector('[data-variant-scroll]');
      if (!button && rail.querySelectorAll('[data-globo-variant-thumbnail]').length > 1) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'collino-variant-scroll';
        button.dataset.variantScroll = 'true';
        container.append(button);
      }
      if (button) {
        const symbol = '›';
        if (button.textContent !== symbol) button.textContent = symbol;
        button.setAttribute('aria-label', 'Select next variant');
        button.hidden = rail.querySelectorAll('[data-globo-variant-thumbnail]').length < 2;
      }
    });
  };

  // One handler per grid, instead of copying this code into every thumbnail.
  const onThumbnailSelect = (event) => {
    const button = event.target.closest?.('#gf-products [data-globo-variant-thumbnail]');
    const card = button?.closest('.spf-product-card');
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();
    const { imageSrc, variantId, imageId } = button.dataset;
    const images = [...card.querySelectorAll('.spf-product-card__image')];
    const image = images.find((item) =>
      (imageId && item.dataset.imageId === imageId) ||
      (variantId && (item.getAttribute('data-variants') || '').split(',').includes(variantId))
    ) || images[0];
    if (image && imageSrc) {
      images.forEach((item) => {
        item.style.opacity = '0';
        item.classList.remove('spf-product-card__image-main');
        item.classList.add('spf-product-card__image-hidden');
      });
      image.src = imageSrc;
      image.dataset.src = imageSrc;
      image.dataset.imageSrc = imageSrc;
      image.removeAttribute('srcset');
      image.classList.remove('spf-product-card__image-hidden');
      image.classList.add('spf-product-card__image-main');
      image.style.opacity = '1';
      image.style.display = '';
    }
    card.querySelectorAll('[data-globo-variant-thumbnail]').forEach((item) => {
      item.classList.toggle('is-active', item === button);
      item.setAttribute('aria-pressed', String(item === button));
    });
    const select = card.querySelector('.spf-product__variants');
    if (select && variantId && select.value !== variantId) {
      select.value = variantId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (variantId) {
      card.querySelectorAll('.spf-product-card__title a, a.spf-product-card__image-wrapper, .spf-product__form-btn-options').forEach((link) => {
        const url = new URL(link.href, window.location.href);
        url.searchParams.set('variant', variantId);
        link.href = url.pathname + url.search + url.hash;
      });
    }
  };

  const onThumbnailPreload = (event) => {
    const button = event.target.closest?.('#gf-products [data-globo-variant-thumbnail]');
    if (!button?.dataset.imageSrc || button.dataset.preloaded) return;
    button.dataset.preloaded = '1';
    const image = new Image();
    image.src = button.dataset.imageSrc;
  };

  // Native history handles cached pages. On a rebuilt page its early scroll
  // restoration can be clamped before Globo has rendered the full list.
  const initListReturn = () => {
    const key = 'collino:list-return:v1';
    const grids = '#gf-products, [data-vc-grid]';
    const cards = '#gf-products .spf-product-card, [data-vc-grid] .spf-product-card';
    const links = '#gf-products .spf-product-card__title a, [data-vc-grid] .spf-product-card__title a';
    let pending = null, scheduled = false, generation = 0, observer = null, timeout = null;
    let pageShown = document.readyState === 'complete';
    const clear = () => { try { sessionStorage.removeItem(key); } catch {} };
    const cancel = () => {
      pending = null;
      generation++;
      scheduled = false;
      observer?.disconnect(); observer = null;
      clearTimeout(timeout); timeout = null;
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => document.removeEventListener(type, cancel, true));
    };
    const restore = () => {
      if (!pending || !pageShown || scheduled) return;
      if (location.href !== pending.url) { cancel(); return; }
      if (!document.querySelector(cards) || document.querySelector('volantes-collection[aria-busy="true"], volantes-collection[data-vc-pending]')) return;
      // No consumir una grilla provisional de otra página: esperar el producto de salida.
      const link = [...document.querySelectorAll(links)].find(item => new URL(item.href, location.href).pathname === pending.anchor);
      if (!link) return;
      scheduled = true;
      const token = generation;
      const ready = document.fonts?.ready || Promise.resolve();
      ready.catch(() => {}).then(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (token !== generation) return;
        scheduled = false;
        if (!pending || location.href !== pending.url) return;
        if (!link.isConnected) { restore(); return; }
        const card = link.closest('.spf-product-card');
        if (!card || document.querySelector('volantes-collection[aria-busy="true"], volantes-collection[data-vc-pending]')) return;
        const y = window.scrollY + card.getBoundingClientRect().top - pending.offset;
        cancel();
        window.scrollTo({ top: Math.max(0, y), left: 0, behavior: 'instant' });
      })));
    };
    const onShow = event => {
      let state;
      try { state = JSON.parse(sessionStorage.getItem(key)); } catch { clear(); return; }
      if (!state || state.url !== location.href) return;
      if (event.persisted) { clear(); cancel(); return; }
      if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') { clear(); return; }
      clear();
      if (!Number.isFinite(state.y) || state.y < 0 || !Number.isFinite(state.offset) || typeof state.anchor !== 'string'
        || !Number.isFinite(state.time) || Date.now() - state.time > 30 * 60 * 1000 || state.time > Date.now()) return;
      cancel(); pending = state;
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => document.addEventListener(type, cancel, { capture: true, passive: true }));
      // Sólo observa mientras existe un regreso pendiente; cubre render y fin de carga nativos.
      const root = document.querySelector('main');
      if (root) {
        observer = new MutationObserver(restore);
        observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy', 'data-vc-pending'] });
      }
      timeout = setTimeout(cancel, 20000);
      restore();
    };
    document.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link?.closest(grids)) return;
      const card = link.closest('.spf-product-card');
      if (!card || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      try {
        sessionStorage.setItem(key, JSON.stringify({ url: location.href, y: window.scrollY,
          anchor: url.pathname, offset: card.getBoundingClientRect().top, time: Date.now() }));
      } catch {}
    }, true);
    window.addEventListener('pageshow', event => {
      pageShown = true;
      onShow(event);
      restore();
    });
    window.addEventListener('pagehide', cancel);
    document.addEventListener('shopify:section:unload', cancel);
    onShow({ persisted: false });
    return restore;
  };

  /* ------------------------------------------------------------------ init */

  const init = () => {
    if (document.body.dataset.globoBridgeInit === 'true') return;
    document.body.dataset.globoBridgeInit = 'true';
    const restoreListReturn = initListReturn();

    // Delegacion: sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
    document.addEventListener('submit', onSubmit);
    document.addEventListener('change', onVariantChange);
    document.addEventListener('click', onCollectionSearchClear, true);
    window.addEventListener('keydown', (event) => {
      if (!event.isComposing && ['Enter', ' '].includes(event.key) && event.target.matches?.('#gf-controls-container .gf-controls-clear-button')) onCollectionSearchClear(event);
    }, true);
    document.addEventListener('input', (event) => { if (event.target.id === 'gf-controls-search-input') syncCollectionFilterAppearance(); });
    document.addEventListener('pointerdown', onThumbnailSelect, true);
    document.addEventListener('click', onThumbnailSelect, true);
    document.addEventListener('pointerover', onThumbnailPreload);
    document.addEventListener('focusin', onThumbnailPreload);
    syncGridControls();
    syncVariantOverflow();
    let variantFrame = 0;
    const scheduleVariantOverflow = () => {
      if (variantFrame) return;
      variantFrame = requestAnimationFrame(() => {
        variantFrame = 0;
        syncVariantOverflow();
      });
    };
    window.addEventListener('resize', scheduleVariantOverflow);
    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-variant-scroll]');
      if (!button) return;
      const container = button.closest('.spf-product-card__thumbnails-container');
      const rail = container?.querySelector('.spf-product-card__thumbnails');
      if (!rail) return;
      event.preventDefault();
      const options = [...rail.querySelectorAll('[data-globo-variant-thumbnail]')];
      if (!options.length) return;
      const current = options.findIndex(option => option.getAttribute('aria-pressed') === 'true');
      const next = options[(current + 1) % options.length];
      next.click();
      const target = next.getBoundingClientRect(), bounds = rail.getBoundingClientRect();
      rail.scrollTo({ left: rail.scrollLeft + target.left - bounds.left - (rail.clientWidth - target.width) / 2, behavior: 'auto' });
      button.focus({ preventScroll: true });
      scheduleVariantOverflow();
    });
    document.addEventListener('scroll', (event) => {
      if (event.target.matches?.('.spf-product-card__thumbnails')) scheduleVariantOverflow();
    }, true);
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(() => {
        restoreListReturn();
        syncSearchClear();
        ensureAscendingSort();
        syncPagination();
        scheduleVariantOverflow();
      }).observe(container, { childList: true, subtree: true });
    }
    window.addEventListener('globoFilterRenderCompleted', () => {
      restoreListReturn();
      scheduleVariantOverflow();
      ensureAscendingSort();
      syncPagination();
    });
    window.addEventListener('popstate', scheduleGridControls);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
